import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/features/auth/session-provider';
import { CaremoryMark } from '@/components/icons/caremory-mark';

import { listProfiles } from '@/features/profiles/lib/profileQueries';
import { ProfileCard } from '@/features/profiles/components/profile-card';
import {
  listActiveMedCountsByProfile,
  listTimelineStatsByProfile,
  type ProfileTimelineStats,
} from '@/features/timeline/lib/timelineQueries';
import { formatRelative } from '@/features/timeline/lib/formatDate';
import { SkeletonCard } from '@/components/skeleton';
import type { Profile } from '@/types/db';

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      profiles: Profile[];
      medCounts: Record<string, number>;
      stats: Record<string, ProfileTimelineStats>;
    }
  | { kind: 'error'; message: string };

export function HomePage() {
  useSession();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listProfiles(),
      listActiveMedCountsByProfile(),
      listTimelineStatsByProfile(),
    ])
      .then(([profiles, medCounts, stats]) => {
        if (cancelled) return;
        setState({ kind: 'ready', profiles, medCounts, stats });
      })
      .catch((err) => {
        console.error('[home] listProfiles failed', err);
        if (cancelled) return;
        setState({
          kind: 'error',
          message: "Couldn't load profiles. Refresh to try again.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const aggregate = useMemo(() => {
    if (state.kind !== 'ready') return null;
    let totalRecords = 0;
    let latest: string | null = null;
    for (const s of Object.values(state.stats)) {
      totalRecords += s.count;
      if (s.latest && (!latest || s.latest > latest)) latest = s.latest;
    }
    return {
      totalRecords,
      latest,
      members: state.profiles.length,
    };
  }, [state]);

  return (
    <main className="min-h-screen safe-top safe-bottom anim-fade-in">
      <header className="relative overflow-hidden border-b border-border bg-gradient-to-b from-accent-soft/60 to-surface">
        <div className="px-4 pt-4 pb-5 max-w-md mx-auto flex items-center gap-3">
          <div className="flex-1 min-w-0 flex flex-col">
            <CaremoryMark variant="primary" className="h-7 self-start" title="Caremory" />
            <p className="text-[11px] font-semibold text-ink-muted tracking-wide mt-0.5">
              Care that remembers
            </p>
          </div>
          <Link
            to="/settings"
            aria-label="Settings"
            className="h-10 w-10 rounded-full bg-surface border border-border text-ink-muted hover:text-ink active:bg-canvas flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </Link>
        </div>
        {aggregate && aggregate.totalRecords > 0 && (
          <div className="px-4 pb-4 max-w-md mx-auto">
            <p className="text-xs text-ink-muted">
              <span className="font-semibold text-ink">
                {aggregate.totalRecords}{' '}
                {aggregate.totalRecords === 1 ? 'record' : 'records'}
              </span>{' '}
              across{' '}
              {aggregate.members}{' '}
              {aggregate.members === 1 ? 'family member' : 'family members'}
              {aggregate.latest && (
                <> · last updated {formatRelative(aggregate.latest)}</>
              )}
            </p>
          </div>
        )}
      </header>

      <section className="px-4 py-6 max-w-md mx-auto">
        <h2 className="text-base font-bold mb-3">Family</h2>

        {state.kind === 'loading' && (
          <ul className="space-y-3" aria-busy="true">
            {[0, 1].map((i) => (
              <li key={i}>
                <SkeletonCard />
              </li>
            ))}
          </ul>
        )}

        {state.kind === 'error' && (
          <div className="card p-6 text-center">
            <p className="text-sm text-flag-block">{state.message}</p>
          </div>
        )}

        {state.kind === 'ready' && state.profiles.length === 0 && (
          <div className="card p-6 text-center">
            <p className="font-bold mb-1">No profiles yet</p>
            <p className="text-sm text-ink-muted mb-4">
              Add a family member to start a timeline of their health records.
            </p>
            <Link to="/profile/new" className="btn-primary inline-flex">
              Add first family member
            </Link>
          </div>
        )}

        {state.kind === 'ready' && state.profiles.length > 0 && (
          <ul className="space-y-3">
            {state.profiles.map((profile, idx) => {
              const s = state.stats[profile.id];
              return (
                <li key={profile.id} className="anim-stagger-in" style={{ animationDelay: `${idx * 60}ms` }}>
                  <ProfileCard
                    profile={profile}
                    activeMedCount={state.medCounts[profile.id] ?? 0}
                    recordCount={s?.count ?? 0}
                    lastUpdated={s?.latest ?? null}
                  />
                </li>
              );
            })}
            <li>
              <Link
                to="/profile/new"
                className="min-h-tap flex items-center justify-center gap-2 p-4 rounded-card border-2 border-dashed border-border text-ink-muted hover:text-accent hover:border-accent active:bg-accent-soft/40 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span className="text-sm font-medium">Add family member</span>
              </Link>
            </li>
          </ul>
        )}
      </section>
    </main>
  );
}
